// src/components/outputs/pieces/PieceCounter.jsx — the character counter of a piece.
//
// Prints what the destination box will see: "N caratteri" when the piece has
// no limit, "N / max" when it has one (src/publish/piece.js :: LIMITS) —
// coloured verde while N ≤ max and errore once over it. A limit with a
// `visible` fold (Facebook's 477, Instagram's 125) adds the muted hint
// "primi {{visible}} visibili". Nothing is ever cut: the counter only says.
//
// The caller decides what N counts (the ready block counts the exact text its
// button copies; a row counts pieceText of its piece); a non-numeric count
// renders nothing, a missing limit shows the plain count — null means unknown.
//
//   PieceCounter({ count, limit, t })   limit = { max, visible? } | null

import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_MONO, SEMANTIC } from '../../../constants/theme.js';

const positive = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

const PieceCounter = ({ count, limit, t = {} }) => {
  const { t: tr } = useTranslation('outputs');
  const n = typeof count === 'number' && Number.isFinite(count) ? count : null;
  if (n == null) return null;

  const max = positive(limit?.max);
  const visible = positive(limit?.visible);
  const over = max != null && n > max;
  const muted = t.textMuted || COLORS.textTertiary;

  const color = max == null ? muted : over ? SEMANTIC.errore.fg : SEMANTIC.verde.fg;
  const label = max == null ? tr('pieces.ui.chars', { n }) : tr('pieces.ui.charsOfMax', { n, max });

  return (
    <span
      data-testid="piece-counter"
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '8px',
        fontSize: '11px',
        fontFamily: FONT_MONO,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        data-state={max == null ? 'plain' : over ? 'over' : 'under'}
        title={over ? tr('pieces.ui.overLimit', { max }) : undefined}
        style={{ color, fontWeight: max == null ? 500 : 700 }}
      >
        {label}
      </span>
      {visible != null && (
        <span style={{ color: muted, fontWeight: 400 }}>
          {tr('pieces.ui.visibleHint', { n: visible })}
        </span>
      )}
    </span>
  );
};

export default PieceCounter;
